import type { OnInit } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  inject,
} from '@angular/core';
import { PstnErrorModalType } from '@freelancer/pstn';
import { ModalRef, UiModule } from '@freelancer/ui';
import { ButtonColor } from '@freelancer/ui/button';

@Component({
  standalone: true,
  imports: [UiModule],
  selector: 'app-pstn-error-modal',
  template: `
    <div
      class="PstnErrorModal"
      flTrackingSection="PstnErrorModal"
    >
      <h2 class="text-body-24 mb-xsmall">{{ heading }}</h2>
      <p class="mb-small">{{ message }}</p>
      <div class="PstnErrorModal-actions">
        <fl-button
          i18n="OK button"
          flTrackingLabel="PstnErrorModal.OkClick"
          [color]="ButtonColor.PRIMARY"
          (click)="handleOkClick()"
        >
          OK
        </fl-button>
      </div>
    </div>
  `,
  styleUrls: ['./pstn-error-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PstnErrorModalComponent implements OnInit {
  private modalRef = inject<ModalRef<PstnErrorModalComponent>>(ModalRef);

  ButtonColor = ButtonColor;

  @Input({ required: true }) errorType: PstnErrorModalType;

  heading = '';
  message = '';

  ngOnInit(): void {
    switch (this.errorType) {
      case PstnErrorModalType.CannotStartPstnFromThread:
        this.heading = $localize`:PSTN cannot start title:Can't start call`;
        this.message = $localize`:PSTN cannot start body:Phone calls aren't available from here. Try again from the project or from your chat with the other person.`;
        break;
      case PstnErrorModalType.AssignProxyForbidden:
        this.heading = $localize`:PSTN forbidden title:This call isn't allowed`;
        this.message = $localize`:PSTN forbidden body:Phone calls aren't available for this project yet. This usually happens if the project isn't active or you don't have permission to call the other person.`;
        break;
      case PstnErrorModalType.AssignProxyNotFound:
        this.heading = $localize`:PSTN not found title:Project not found`;
        this.message = $localize`:PSTN not found body:We couldn't find the project linked to this chat. It may have been removed or you may no longer have access.`;
        break;
      case PstnErrorModalType.AssignProxyFailed:
        this.heading = $localize`:PSTN error title:Unable to set up your call`;
        this.message = $localize`:PSTN error body:Something went wrong while setting up your call. Please try again in a moment.`;
        break;
      default:
        this.heading = $localize`:PSTN error generic title:Something went wrong`;
        this.message = $localize`:PSTN error generic body:We couldn't complete your call. Please try again, or contact support if this keeps happening.`;
        break;
    }
  }

  handleOkClick(): void {
    this.modalRef.close();
  }
}
